import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Loader2, Send, Play, Users, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { FILTER_LABELS, FILTER_HINTS, FILTER_OPTIONS_BY_CAMPAIGN } from './triggers';

/** Invio manuale subito: usa il modello WhatsApp già approvato per questa tipologia. */
const ManualCampaignCard = ({ preset, waConnected, onSent }) => {
  const { API, getAuthHeaders } = useAuth();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState(preset?.default_filter || '');
  const [serviceId, setServiceId] = useState('');
  const [services, setServices] = useState([]);
  const [customVars, setCustomVars] = useState({});
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);

  const start = () => {
    setFilter(preset.default_filter);
    setServiceId('');
    const defaults = {};
    (preset.custom_fields || []).forEach((f) => { defaults[f.key] = f.default || ''; });
    setCustomVars(defaults);
    setPreview(null);
    setOpen(true);
  };

  useEffect(() => {
    if (filter !== 'service' || services.length) return;
    axios.get(`${API}/services`, getAuthHeaders())
      .then((res) => setServices((res.data || []).filter((s) => s.is_active !== false)))
      .catch(() => setServices([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const fetchPreview = useCallback(async () => {
    if (!open) return;
    try {
      const res = await axios.post(`${API}/marketing/whatsapp/campaign/preview`,
        { campaign_id: preset.id, filter, custom_vars: customVars, service_id: serviceId || null }, getAuthHeaders());
      setPreview(res.data);
    } catch {
      setPreview(null);
    }
  }, [API, getAuthHeaders, open, preset, filter, serviceId, customVars]);

  useEffect(() => { fetchPreview(); }, [fetchPreview]);

  const send = async () => {
    setSending(true);
    try {
      const res = await axios.post(`${API}/marketing/whatsapp/campaign/send`,
        { campaign_id: preset.id, filter, custom_vars: customVars, service_id: serviceId || null }, getAuthHeaders());
      toast.success('Campagna inviata', { description: `${res.data.sent} consegnati · ${res.data.failed} non riusciti.` });
      setOpen(false);
      onSent();
    } catch (err) {
      toast.error('Invio non riuscito', { description: err.response?.data?.detail || err.message });
    } finally {
      setSending(false);
    }
  };

  const filterOptions = FILTER_OPTIONS_BY_CAMPAIGN[preset.id] || [preset.default_filter];
  const missingFields = (preset.custom_fields || []).filter((f) => !String(customVars[f.key] ?? '').trim());

  return (
    <Card data-testid={`manual-campaign-${preset.id}`}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-outfit">Invia adesso a un gruppo di clienti</CardTitle>
        <CardDescription>
          {preset.description} Usa il modello WhatsApp approvato <span className="font-mono text-xs">{preset.template_name}</span>,
          quindi arriva anche a chi non ti scrive da giorni.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button onClick={start} disabled={!waConnected} className="w-full sm:w-auto min-h-[44px]"
          data-testid={`open-campaign-${preset.id}`}>
          <Play className="w-4 h-4 mr-2" />Avvia invio manuale
        </Button>
        {!waConnected && (
          <p className="text-xs text-muted-foreground mt-2">Collega WhatsApp in Impostazioni → Social per usarlo.</p>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="campaign-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit">{preset.title}</DialogTitle>
            <DialogDescription>{preset.description}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {filterOptions.length > 1 && (
              <div className="space-y-1.5">
                <Label>A chi lo mando</Label>
                <Select value={filter} onValueChange={(v) => { setFilter(v); setServiceId(''); }}>
                  <SelectTrigger data-testid="campaign-filter-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {filterOptions.map((f) => (
                      <SelectItem key={f} value={f} data-testid={`campaign-filter-${f}`}>{FILTER_LABELS[f] || f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {FILTER_HINTS[filter] && (
                  <p className="text-xs text-muted-foreground" data-testid="campaign-filter-hint">{FILTER_HINTS[filter]}</p>
                )}
                {filter === 'service' && (
                  <Select value={serviceId} onValueChange={setServiceId}>
                    <SelectTrigger data-testid="campaign-service-select">
                      <SelectValue placeholder="Scegli il servizio" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((s) => (
                        <SelectItem key={s.id} value={s.id} data-testid={`campaign-service-${s.id}`}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            {(preset.custom_fields || []).map((f) => {
              const empty = !String(customVars[f.key] ?? '').trim();
              return (
                <div key={f.key} className="space-y-1.5">
                  <Label htmlFor={`field-${f.key}`}>
                    {f.label} <span className="text-destructive">*</span>
                  </Label>
                  <Input id={`field-${f.key}`}
                    type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
                    value={customVars[f.key] || ''}
                    onChange={(e) => setCustomVars((v) => ({ ...v, [f.key]: e.target.value }))}
                    className={empty ? 'border-destructive focus-visible:ring-destructive' : ''}
                    data-testid={`field-${f.key}`} />
                </div>
              );
            })}

            {missingFields.length > 0 && (
              <div className="flex items-start gap-2 rounded-md bg-destructive/10 border border-destructive/30 p-2.5"
                data-testid="campaign-missing-fields">
                <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                <p className="text-xs text-destructive">
                  Il modello non parte finché non compili: {missingFields.map((f) => f.label).join(', ')}.
                </p>
              </div>
            )}

            <div className="rounded-lg bg-primary/5 border border-primary/20 p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground">Destinatari</p>
                  <p className="font-semibold text-lg" data-testid="campaign-preview-count">
                    {preview ? `${preview.count} client${preview.count === 1 ? 'e' : 'i'}` : <Loader2 className="w-4 h-4 animate-spin inline" />}
                  </p>
                </div>
                <Users className="w-8 h-8 text-primary/60" />
              </div>
              {preview?.sample_customer && (
                <p className="text-[11px] text-muted-foreground mt-2">
                  Esempio: <span className="font-medium text-foreground">{preview.sample_customer}</span> riceverà il modello con i tuoi dati già compilati.
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button variant="outline" className="min-h-[44px]" onClick={() => setOpen(false)} disabled={sending}>Annulla</Button>
            <Button onClick={send} disabled={sending || !preview || preview.count === 0 || missingFields.length > 0 || (filter === 'service' && !serviceId)}
              className="min-h-[44px]" data-testid="campaign-send-btn">
              {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Invio...</> : <><Send className="w-4 h-4 mr-2" />Invia a {preview?.count || 0}</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ManualCampaignCard;
